import React, { useState, useEffect } from 'react';
import { usePlayers } from '../contexts/PlayerContext';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Layout } from '../components/layout/Layout';
import { apiClient } from '../api/client';
import { TeamSize, TeamPlayer } from '../types';
import { getBalanceQuality } from '../utils/calculations';

export function ManualBuilder() {
  const { players, fetchPlayers } = usePlayers();

  useEffect(() => {
    fetchPlayers();
  }, []);
  const [teamSize, setTeamSize] = useState<TeamSize>(7);
  const [teamA, setTeamA] = useState<string[]>([]);
  const [teamB, setTeamB] = useState<string[]>([]);
  const [matchDate, setMatchDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');

  const availablePlayers = players
    .filter(
      (p) => !teamA.includes(p.id) && !teamB.includes(p.id) && p.calculated !== null && p.calculated.overall_rating !== null
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const teamAPlayers = players.filter((p) => teamA.includes(p.id));
  const teamBPlayers = players.filter((p) => teamB.includes(p.id));

  const addToTeamA = (playerId: string) => {
    if (teamA.length < teamSize) {
      setTeamA([...teamA, playerId]);
    }
  };

  const addToTeamB = (playerId: string) => {
    if (teamB.length < teamSize) {
      setTeamB([...teamB, playerId]);
    }
  };

  const removeFromTeamA = (playerId: string) => {
    setTeamA(teamA.filter((id) => id !== playerId));
  };

  const removeFromTeamB = (playerId: string) => {
    setTeamB(teamB.filter((id) => id !== playerId));
  };

  const calculateStats = (playerIds: string[]) => {
    const teamPlayers = players.filter((p) => playerIds.includes(p.id));
    const totalRating = teamPlayers.reduce((sum, p) => sum + (p.calculated?.overall_rating || 0), 0);
    const avgRating = teamPlayers.length > 0 ? totalRating / teamPlayers.length : 0;

    return {
      total_rating: totalRating,
      avg_rating: avgRating,
      count: teamPlayers.length,
    };
  };

  const statsA = calculateStats(teamA);
  const statsB = calculateStats(teamB);
  const ratingDifference = Math.abs(statsA.avg_rating - statsB.avg_rating);
  const balanceInfo = teamA.length === teamSize && teamB.length === teamSize
    ? getBalanceQuality(ratingDifference)
    : null;

  const handleSaveMatch = async () => {
    if (teamA.length !== teamSize || teamB.length !== teamSize) {
      setError(`Both teams must have exactly ${teamSize} players`);
      return;
    }

    try {
      setError('');

      // Build team data
      const buildTeam = (playerIds: string[]): TeamPlayer[] => {
        return playerIds.map((id) => {
          const player = players.find((p) => p.id === id)!;
          return {
            player_id: player.id,
            name: player.name,
            overall_rating: player.calculated!.overall_rating,
            assigned_position: player.calculated!.best_position,
          };
        });
      };

      const teamAData = buildTeam(teamA);
      const teamBData = buildTeam(teamB);

      // Calculate position statistics for each team
      const calcPositionStats = (teamData: TeamPlayer[]) => {
        const fwdPlayers = teamData.filter(p => p.assigned_position === 'FWD');
        const midPlayers = teamData.filter(p => p.assigned_position === 'MID');
        const defPlayers = teamData.filter(p => p.assigned_position === 'DEF');
        const gkPlayers = teamData.filter(p => p.assigned_position === 'GK');

        const avgFwd = fwdPlayers.length > 0
          ? fwdPlayers.reduce((sum, p) => sum + p.overall_rating, 0) / fwdPlayers.length
          : 0;
        const avgMid = midPlayers.length > 0
          ? midPlayers.reduce((sum, p) => sum + p.overall_rating, 0) / midPlayers.length
          : 0;
        const avgDef = defPlayers.length > 0
          ? defPlayers.reduce((sum, p) => sum + p.overall_rating, 0) / defPlayers.length
          : 0;
        const avgGk = gkPlayers.length > 0
          ? gkPlayers.reduce((sum, p) => sum + p.overall_rating, 0) / gkPlayers.length
          : undefined;

        return {
          avg_fwd: avgFwd,
          avg_mid: avgMid,
          avg_def: avgDef,
          avg_gk: avgGk,
          fwd_count: fwdPlayers.length,
          mid_count: midPlayers.length,
          def_count: defPlayers.length,
        };
      };

      const posStatsA = calcPositionStats(teamAData);
      const posStatsB = calcPositionStats(teamBData);

      const matchData = {
        date: new Date(matchDate).toISOString(),
        team_size: teamSize,
        generation_method: 'MANUAL' as const,
        teams: {
          team_a: teamAData,
          team_b: teamBData,
        },
        statistics: {
          team_a: {
            ...statsA,
            ...posStatsA,
          },
          team_b: {
            ...statsB,
            ...posStatsB,
          },
        },
        balance: {
          quality: balanceInfo!.quality as any,
          rating_difference: ratingDifference,
        },
      };

      console.log('=== MANUAL BUILDER - SAVING MATCH ===');
      console.log('Match data being sent:', JSON.stringify(matchData, null, 2));

      await apiClient.saveMatch(matchData);

      alert('Match saved to history!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save match');
    }
  };

  const handleClear = () => {
    setTeamA([]);
    setTeamB([]);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Manual Team Builder</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manually assign players to teams
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Configuration */}
        <Card>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-1 sm:flex-initial">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Team Size
                </label>
                <select
                  value={teamSize}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value) as TeamSize;
                    setTeamSize(newSize);
                    // Trim teams if new size is smaller
                    if (teamA.length > newSize) {
                      setTeamA(teamA.slice(0, newSize));
                    }
                    if (teamB.length > newSize) {
                      setTeamB(teamB.slice(0, newSize));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={5}>5v5</option>
                  <option value={6}>6v6</option>
                  <option value={7}>7v7</option>
                  <option value={8}>8v8</option>
                </select>
              </div>

              <div className="flex-1 sm:flex-initial">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Match Date
                </label>
                <input
                  type="date"
                  value={matchDate}
                  onChange={(e) => setMatchDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={handleClear} className="flex-1 sm:flex-initial">
                Clear All
              </Button>
              <Button
                onClick={handleSaveMatch}
                disabled={teamA.length !== teamSize || teamB.length !== teamSize}
                className="flex-1 sm:flex-initial"
              >
                Save Match
              </Button>
            </div>
          </div>

          {balanceInfo && (
            <div className={`mt-4 text-sm font-medium ${balanceInfo.color}`}>
              {balanceInfo.emoji} {balanceInfo.quality} Balance
              <span className="text-gray-600 dark:text-gray-400 ml-1">
                ({ratingDifference.toFixed(1)} difference)
              </span>
            </div>
          )}
        </Card>

        {/* Teams Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Team A */}
          <ManualTeamCard
            teamName="Team A"
            color="blue"
            players={teamAPlayers}
            teamSize={teamSize}
            stats={statsA}
            onRemove={removeFromTeamA}
          />

          {/* Available Players */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">Available Players</h2>
            {players.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No players available. Add some players first!
              </div>
            ) : availablePlayers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                All players assigned
              </div>
            ) : (
              <div className="space-y-2">
                {availablePlayers.map((player) => (
                  <div
                    key={player.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                  >
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{player.name}</div>
                      <div className="text-xs text-gray-500">
                        {player.calculated?.best_position || '-'} •{' '}
                        {player.calculated?.overall_rating?.toFixed(1) || '-'}
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => addToTeamA(player.id)}
                        disabled={teamA.length >= teamSize}
                      >
                        A ←
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => addToTeamB(player.id)}
                        disabled={teamB.length >= teamSize}
                      >
                        → B
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Team B */}
          <ManualTeamCard
            teamName="Team B"
            color="red"
            players={teamBPlayers}
            teamSize={teamSize}
            stats={statsB}
            onRemove={removeFromTeamB}
          />
        </div>
      </div>
    </Layout>
  );
}

// Manual Team Card Component
function ManualTeamCard({
  teamName,
  color,
  players,
  teamSize,
  stats,
  onRemove,
}: {
  teamName: string;
  color: 'blue' | 'red';
  players: any[];
  teamSize: number;
  stats: any;
  onRemove: (playerId: string) => void;
}) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-500',
    red: 'bg-red-50 border-red-500',
  };

  const headerColors = {
    blue: 'bg-blue-600',
    red: 'bg-red-600',
  };

  return (
    <Card padding={false} className={`border-2 ${colorClasses[color]}`}>
      <div className={`${headerColors[color]} text-white px-4 py-3`}>
        <h3 className="text-xl font-bold">
          {teamName} ({players.length}/{teamSize})
        </h3>
      </div>

      <div className="p-4 space-y-4">
        {players.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No players assigned
          </div>
        ) : (
          <div className="space-y-2">
            {players.map((player) => (
              <div
                key={player.id}
                className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0"
              >
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{player.name}</div>
                  <div className="text-xs text-gray-500">
                    {player.calculated?.best_position} •{' '}
                    {player.calculated?.overall_rating.toFixed(1)}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(player.id)}
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Statistics */}
        {players.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Total Rating:</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.total_rating.toFixed(1)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Average Rating:</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.avg_rating.toFixed(1)}</span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
